import { useMemo, useState, useEffect, useCallback } from "react";
import { useUserDataFlattened } from "../../../modules/user/stores";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { ROUTES } from "@/types/routes";
import {
  useActivityPopupPriority,
  useIsActivityQualificationAutoEnabled,
  useIsHomeDisplayReady,
  useActivities,
  isActivityIdValid,
  OPEN_RESCUE_FUND_EVENT,
} from "@/modules/homeDisplay/hooks";
import { ActivityType, TimingType } from "@/modules/homeDisplay/types";
// 导入简单的弹窗组件
import { RescueFundPopup } from "../components/RescueFund/RescueFundPopup";
// 导入救援金 API
import { rescueFundApi } from "@/api/rescueFund";

// 救援金弹窗配置（如果 POPUP_CONFIGS 中没有，使用默认值）
const RESCUE_FUND_CONFIG = POPUP_CONFIGS.RESCUE_FUND || {
  id: "rescue-fund",
  priority: 3,
};

const POPUP_STORAGE_KEY = "shown_popup_records";

/**
 * 检查今天是否已打开过救援金弹窗（与 useQueuedPopup 写入的 key 一致：rescue-fund_${userId}）
 */
const hasOpenedRescueFundPopupToday = (userId: string): boolean => {
  try {
    const records = localStorage.getItem(POPUP_STORAGE_KEY);
    if (!records) return false;
    const parsed = JSON.parse(records) as Record<
      string,
      { time: string; timestamp: number }
    >;
    const key = `rescue-fund_${userId}`;
    if (!parsed[key]) return false;
    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    return parsed[key].time === todayString;
  } catch {
    return false;
  }
};

export interface RescueFundPopupResult {
  shouldShow: boolean;
  trigger: (taskId?: string) => void;
  /** 是否显示感叹号（任一 task 可领时 true，兼容旧用法） */
  shouldShowBadge: boolean;
  /** 按 task 判断：可领取时返回 true，用于浮窗显示角标 */
  getShouldShowBadge: (activityId: string) => boolean;
  /** 可领取时：今天没打开过弹窗返回 undefined（显示感叹号），今天已打开过返回 1（显示数字 1） */
  getBadgeCount: (activityId: string) => number | undefined;
  remainingMs: number;
  /** 该活动是否适用于当前用户（如 VIP 反水包含当前 VIP）；不适用则不展示浮窗 */
  isActivityAvailable?: (activityId: string) => boolean;
}

export const useRescueFundPopup = (): RescueFundPopupResult => {
  const { data: userInfo, userId } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();
  const activities = useActivities();

  // 获取救援金活动数据
  const rescueFundActivities = useMemo(() => {
    return activities?.RESCUE_FUND?.list || [];
  }, [activities]);

  // 用于存储当前选中的任务ID
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  // 用于存储等待触发的任务ID
  const [pendingTaskId, setPendingTaskId] = useState<string | null>(null);
  // 按 activityId 存储该 task 是否当前可领取（仅用于浮窗感叹号）
  const [claimableByActivityId, setClaimableByActivityId] = useState<
    Record<string, boolean>
  >({});
  // 按 activityId 标记「不适用于当前用户」（如 VIP 反水配置不包含当前 VIP），此类活动不展示
  const [activityIdsUnavailable, setActivityIdsUnavailable] = useState<
    Record<string, boolean>
  >({});

  // 仅展示「适用于当前用户」的活动（如 VIP 反水配置包含当前 VIP）；不适用的不展示
  const rescueFundActivitiesAvailable = useMemo(() => {
    return rescueFundActivities.filter(a => !activityIdsUnavailable[a.id]);
  }, [rescueFundActivities, activityIdsUnavailable]);

  // 根据选中的任务ID获取对应的活动，如果没有选中则取第一个（仅从可用活动中选）
  const rescueFundActivity = useMemo(() => {
    if (selectedTaskId) {
      const found = rescueFundActivitiesAvailable.find(
        activity => activity.id === selectedTaskId
      );
      return found;
    }
    return rescueFundActivitiesAvailable[0];
  }, [rescueFundActivitiesAvailable, selectedTaskId]);

  // 获取活动配置中的 priority，不存在则使用默认值
  const priority = useActivityPopupPriority(
    ActivityType.RESCUE_FUND,
    RESCUE_FUND_CONFIG.priority,
    TimingType.ACTIVITY_QUALIFICATION
  );
  const isAutoEnabled = useIsActivityQualificationAutoEnabled(
    ActivityType.RESCUE_FUND
  );

  // 检查活动是否真实存在于 activities 配置中
  const isActivityValid = useMemo(() => {
    return isActivityIdValid(
      rescueFundActivity?.id,
      ActivityType.RESCUE_FUND,
      activities
    );
  }, [rescueFundActivity?.id, activities]);

  const [badgeRefreshKey, setBadgeRefreshKey] = useState(0);
  // 每次打开弹窗时递增，传给内容组件作为 key，确保重新挂载并实时请求上一期活动数据
  const [popupOpenKey, setPopupOpenKey] = useState(0);

  // 用于触发弹窗的状态
  const [shouldTriggerPopup, setShouldTriggerPopup] = useState(false);

  // 领取成功后刷新浮窗角标（让数字 1 消失）
  const refreshBadge = useCallback(() => {
    setBadgeRefreshKey(prev => prev + 1);
  }, []);

  // 使用简单的弹窗组件
  const RescueFundContent: React.FC<{
    onComplete: () => void;
  }> = ({ onComplete }) => {
    const [visible, setVisible] = useState(true);

    const handleClose = () => {
      setVisible(false);
      onComplete();
      setBadgeRefreshKey(prev => prev + 1);
      // 清空选中的任务ID
      setSelectedTaskId(null);
    };

    const taskId = rescueFundActivity?.id;
    const type = rescueFundActivity?.rescueFundData?.type;

    if (!taskId || !type) {
      return null;
    }

    return (
      <RescueFundPopup
        visible={visible}
        onClose={handleClose}
        taskId={taskId}
        type={type}
        refreshKey={popupOpenKey}
        onClaimSuccess={refreshBadge}
      />
    );
  };

  /**
   * 按 task 分别查询是否「当前可领取」，仅该 task 可领时才显示该浮窗上的感叹号
   * useFloatingIcons 里用 getShouldShowBadge(activityId) 决定每个图标的 badge.show
   */
  useEffect(() => {
    if (!rescueFundActivities.length || !userId) {
      setClaimableByActivityId({});
      setActivityIdsUnavailable({});
      return;
    }
    let isMounted = true;
    const check = async () => {
      try {
        const results = await Promise.all(
          rescueFundActivities.map(async activity => {
            try {
              const response = await rescueFundApi.getUserTaskInfo(activity.id);
              const res = response as any;
              const ok = res?.data?.status === 0 || res?.status === 0;
              const raw = res?.data?.model ?? res?.data ?? res?.model;
              // 接口成功但 model 为空：表示该活动不适用于当前用户（如 VIP 反水不包含当前 VIP），不展示
              const unavailable =
                ok && (raw == null || typeof raw !== "object");
              if (unavailable) return { claimable: false, unavailable: true };
              if (!ok) return { claimable: false, unavailable: false };
              const hasUnclaimed =
                raw.hasUnclaimedSettledTask === true || !!raw.settledTask;
              const canClaimNow = raw.canClaim === true;
              return {
                claimable: hasUnclaimed && canClaimNow,
                unavailable: false,
              };
            } catch (error) {
              console.error(
                `[救援金Badge] 查询活动 ${activity.id} 失败:`,
                error
              );
              return { claimable: false, unavailable: false };
            }
          })
        );
        if (isMounted) {
          const nextClaimable: Record<string, boolean> = {};
          const nextUnavailable: Record<string, boolean> = {};
          rescueFundActivities.forEach((activity, i) => {
            const r = results[i] as {
              claimable: boolean;
              unavailable: boolean;
            };
            const claimable = !!r?.claimable;
            const unavailable = !!r?.unavailable;
            nextClaimable[activity.id] = claimable;
            nextUnavailable[activity.id] = unavailable;
            // 浮窗 banner 可能用 activityId 或 taskGroupId，两套 id 都写入以便 getShouldShowBadge(activityId) 能命中
            const taskGroupId = (activity as any)?.taskGroupId;
            if (taskGroupId && taskGroupId !== activity.id) {
              nextClaimable[taskGroupId] = claimable;
              if (unavailable) nextUnavailable[taskGroupId] = true;
            }
          });
          setClaimableByActivityId(nextClaimable);
          setActivityIdsUnavailable(prev => ({ ...prev, ...nextUnavailable }));
        }
      } catch (error) {
        console.error("[救援金Badge] 查询可领取状态失败:", error);
        if (isMounted) {
          setClaimableByActivityId({});
        }
      }
    };
    check();
    return () => {
      isMounted = false;
    };
  }, [rescueFundActivities, userId, badgeRefreshKey]);

  /**
   * 按活动单独显示角标：仅当该 activityId 对应任务可领取时返回 true（浮窗配置 id 需与 activity.id/taskGroupId 一致）
   */
  const getShouldShowBadge = useCallback(
    (activityId: string) => {
      return claimableByActivityId[activityId] === true;
    },
    [claimableByActivityId, badgeRefreshKey]
  );

  /**
   * 按活动单独显示：该活动可领取时，今天没打开过弹窗 → undefined（感叹号），今天已打开过 → 1（数字 1）
   */
  const getBadgeCount = useCallback(
    (activityId: string) => {
      if (claimableByActivityId[activityId] !== true) return undefined;
      if (!userId) return undefined;
      return hasOpenedRescueFundPopupToday(userId) ? 1 : undefined;
    },
    [claimableByActivityId, userId, badgeRefreshKey]
  );

  /** 该活动是否适用于当前用户（如 VIP 反水包含当前 VIP）；不适用则不展示浮窗 */
  const isActivityAvailable = useCallback(
    (activityId: string) => !activityIdsUnavailable[activityId],
    [activityIdsUnavailable]
  );

  const shouldShowBadge = useMemo(
    () => Object.values(claimableByActivityId).some(Boolean),
    [claimableByActivityId]
  );

  // 检查是否应该显示
  const shouldShow = useMemo(() => {
    return !!rescueFundActivity && isActivityValid;
  }, [rescueFundActivity, isActivityValid]);

  // 计算活动剩余时间
  const remainingMs = useMemo(() => {
    if (!rescueFundActivity?.taskFinishTime) return 0;

    const endTime =
      typeof rescueFundActivity.taskFinishTime === "number"
        ? rescueFundActivity.taskFinishTime
        : new Date(rescueFundActivity.taskFinishTime).getTime();

    const remaining = endTime - Date.now();
    return Math.max(0, remaining);
  }, [rescueFundActivity?.taskFinishTime]);

  // 使用 useQueuedPopup
  const { trigger: originalTrigger } = useQueuedPopup({
    popupId: RESCUE_FUND_CONFIG.id,
    priority,
    shouldShow: () => {
      return isConfigReady && !!rescueFundActivity && isActivityValid;
    },
    renderContent: onComplete => <RescueFundContent onComplete={onComplete} />,
    showOnce: true,
    showOnceGranularity: "day",
    allowManualTrigger: true,
    autoTrigger: isAutoEnabled,
    dependencies: [
      isConfigReady,
      rescueFundActivity,
      isActivityValid,
      selectedTaskId,
      isAutoEnabled,
    ],
    allowedPaths: [
      ROUTES.HOME,
      ROUTES.HOME_ALT,
      "/",
      ROUTES.EVENT_CENTER,
      ROUTES.LOGIN,
      ROUTES.REGISTER,
    ],
    blockedPaths: [
      "/game",
      ROUTES.RECHARGE,
      ROUTES.LOGIN,
      ROUTES.REGISTER,
      ROUTES.WITHDRAW,
    ],
    userBinding: true,
    getUserId: () => userInfo?.userId,
    timingType: TimingType.ACTIVITY_QUALIFICATION,
  });

  // 监听首页 banner 等派发的「打开救援金弹窗」事件，设置 taskId 后由下方 useEffect 打开弹窗
  useEffect(() => {
    const handler = (e: Event) => {
      const taskId = (e as CustomEvent<{ taskId: string }>).detail?.taskId;
      if (taskId) {
        setPendingTaskId(taskId);
      }
    };
    window.addEventListener(OPEN_RESCUE_FUND_EVENT, handler);
    return () => window.removeEventListener(OPEN_RESCUE_FUND_EVENT, handler);
  }, []);

  // 监听 pendingTaskId 的变化，在状态更新后触发弹窗
  useEffect(() => {
    if (pendingTaskId) {
      setSelectedTaskId(pendingTaskId);
      setShouldTriggerPopup(true);
      setPendingTaskId(null);
    }
  }, [pendingTaskId]);

  // 监听 shouldTriggerPopup 的变化，触发弹窗
  useEffect(() => {
    if (shouldTriggerPopup) {
      setShouldTriggerPopup(false);
      setPopupOpenKey(k => k + 1); // 每次打开弹窗递增，让内容组件重新挂载并实时查询上一期数据
      // 延迟一小段时间，确保 selectedTaskId 已经更新
      setTimeout(() => {
        originalTrigger();
      }, 50);
    }
  }, [shouldTriggerPopup, selectedTaskId, originalTrigger]);

  // 包装 trigger 函数，支持传入 taskId
  const trigger = (taskId?: string) => {
    if (taskId) {
      // 设置 pending 状态，让 useEffect 处理

      setPendingTaskId(taskId);
    } else {
      // 没有 taskId，直接触发；每次打开都递增 key，确保实时查询上一期数据

      setPopupOpenKey(k => k + 1);
      originalTrigger();
    }
  };

  return {
    shouldShow,
    trigger,
    shouldShowBadge,
    getShouldShowBadge,
    getBadgeCount,
    remainingMs,
    isActivityAvailable,
  };
};
